import { FC, useState, useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Select,
  MenuItem,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  OutlinedInput,
  ToggleButton,
  ToggleButtonGroup,
  createFilterOptions,
} from "@mui/material";
import GenericJsonBuilderWrapper from "@/components/general/genericJsonBuilderWrapper";
import JsonEditor from "@/components/general/jsonEditor";
import { DropDownAsync } from "@/components/forms/inputs/dropDownAsync";
import { api } from "@/helper/api";
import { useTranslation } from "react-i18next";

type FieldDependency = {
  dependsOn: string;
  condition: (value: any) => boolean;
  action: "reset" | "set" | "hide";
  value?: any;
};

export type FieldOption = {
  key: string;
  type: "boolean" | "string" | "number" | "array" | "object"; // TODO: rename to defaultType for clarity
  operators?: string[] | null;
  operator_aliases?: Record<string, string>;
  placeholder?: string;
  predefined_values?: any[] | null;
  allow_custom_input?: boolean;
  use_multiline?: boolean;
  fields?: FieldOption[];
  key_alias?: string;
  predefined_values_with_aliases?: { key: string; alias: string }[];
  renderKeyAs?: "default" | "textfield";
  // opt-in: offers these keys in a searchable dropdown (free text stays possible)
  key_suggestions?: { value: string; label?: string }[];
  hide?: boolean;
  renderValueInputToggle?: boolean;
  renderValueInputTypeToggle?: boolean;
  valueTypesForInputToggle?: (
    | "string"
    | "number"
    | "array_string"
    | "array_number"
    | "bool"
  )[];
  operatorsExpectingStringValuesOnly?: string[];
  operatorsExpectingObjectValues?: Record<string, FieldOption[]>;
  fixedKeyOperatorGroups?: { fixedKey: string; operators: string[] }[];
  renderValueInputAsSearchableDropdown?: boolean;
  searchableDropdownParameters?: {
    apiEndpoint?: string;
    apiResponseNameToBeDisplayedOnDropdown?: string;
    apiResponseValueForJson?: string;
    customSearchParameter?: string;
    additionalSearchParameters?: any;
  };
  allow_negative_numbers?: boolean;
  useJsonEditor?: boolean;
  jsonEditorParameters?: {
    label?: string;
    noLabel?: boolean;
    noTopBar?: boolean;
    noBottomBar?: boolean;
  };
  is_not_operator?: boolean;
  dependencies?: FieldDependency[];
  entity?: string;
  wrappableInDoubleBraces?: boolean;
};

interface JsonEntry {
  fieldKey: string;
  operator?: string | null;
  value: any;
  customKey?: boolean;
  useCustom?: boolean;
  nestedEntries?: JsonEntry[];
  valueType?: "string" | "number" | "array_string" | "array_number" | "bool";
  hidden?: boolean;
}

export interface GenericJsonBuilderProps {
  initialData?: Record<string, any> | JsonEntry[];
  fieldOptions: FieldOption[] | Record<string, any>;
  label?: string;
  onSave?: (updatedJson: object | string) => void;
  triggerSave?: boolean;
  enableSaveButton?: boolean;
  enableDragDrop?: boolean;
  secondaryButton?: {
    label: string;
    onClick: () => void;
  };
  checkForUnrecognizedFields?: boolean;
  onChange?: (updatedJson: object | string) => void;
  name?: string;
  renderAs: "form" | "dropdown";
  enableDeleteButton?: boolean;
  enableResetButton?: boolean;
  enableAddNewEntryButton?: boolean;
  isArrayOfObjects?: boolean;
  emptyArrayOfObjectsValue?: "emptyArray" | "singleEmptyObject";
  required?: boolean;
  entryLayout?: "vertical" | "horizontal";
}

const useSearchableDropdownLabel = (value: any, fieldOpt: FieldOption) => {
  const [label, setLabel] = useState<any>(value);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (
      !hasInitialized.current &&
      fieldOpt.renderValueInputAsSearchableDropdown &&
      value
    ) {
      hasInitialized.current = true;
      const apiResponseValue =
        fieldOpt.searchableDropdownParameters?.apiResponseValueForJson || "id";
      const apiResponseName =
        fieldOpt.searchableDropdownParameters
          ?.apiResponseNameToBeDisplayedOnDropdown || "name";

      api.genericApiRequest({
        method: "get",
        entity:
          fieldOpt.searchableDropdownParameters?.apiEndpoint ||
          "subject/quicksearch",
        parametersToRender: {
          additionalUrlParameters: {
            [apiResponseValue]: value,
            ...(fieldOpt.searchableDropdownParameters
              ?.additionalSearchParameters || {}),
          },
          depth: "0",
        },
        successHandler: (res: any) => {
          if (res.data?.results?.[0]) {
            setLabel({
              value: value,
              label: res.data.results[0][apiResponseName],
            });
          }
        },
        failHandler: (error: any) => {
          console.error("Error fetching label:", error);
        },
      });
    }
  }, []);

  useEffect(() => {
    hasInitialized.current = false;
  }, [value]);

  return fieldOpt.renderValueInputAsSearchableDropdown ? label : value;
};

interface SearchableDropdownValueInputProps {
  entry: JsonEntry;
  index: number;
  fieldOpt: FieldOption;
  updateEntry: (index: number, updatedFields: Partial<JsonEntry>) => void;
}

const SearchableDropdownValueInput: FC<SearchableDropdownValueInputProps> = ({
  entry,
  index,
  fieldOpt,
  updateEntry,
}) => {
  const { t } = useTranslation();
  const preExistingValue = useSearchableDropdownLabel(entry.value, fieldOpt);

  return (
    <DropDownAsync
      dataCallback={(
        inputValue: string,
        callback: (options: any[]) => void,
      ) => {
        const customSearchParameter =
          fieldOpt.searchableDropdownParameters?.customSearchParameter ||
          "search";
        const additionalUrlParameters = {
          [customSearchParameter]: inputValue,
          ...(fieldOpt.searchableDropdownParameters
            ?.additionalSearchParameters || {}),
        };

        api.genericApiRequest({
          method: "get",
          entity:
            fieldOpt.searchableDropdownParameters?.apiEndpoint ||
            "subject/quicksearch",
          parametersToRender: { additionalUrlParameters, depth: "0" },
          successHandler: (res: any) => {
            const apiResponseName =
              fieldOpt.searchableDropdownParameters
                ?.apiResponseNameToBeDisplayedOnDropdown || "name";
            const apiResponseValue =
              fieldOpt.searchableDropdownParameters?.apiResponseValueForJson ||
              "id";
            const options = (res.data?.results || []).map((item: any) => ({
              value: item[apiResponseValue],
              label: item[apiResponseName],
            }));
            callback(options);
          },
          failHandler: (error: any) => {
            console.error("Error fetching options:", error);
            callback([]);
          },
        });
      }}
      onChange={(selectedOption: any) => {
        const currentValue =
          entry.value &&
          typeof entry.value === "object" &&
          "value" in entry.value
            ? entry.value.value
            : entry.value;
        const newValue =
          selectedOption &&
          typeof selectedOption === "object" &&
          "value" in selectedOption
            ? selectedOption.value
            : selectedOption;

        if (currentValue !== newValue) {
          updateEntry(index, { value: selectedOption });
        }
      }}
      preExistingValue={preExistingValue}
      placeholder={fieldOpt.placeholder || t("search_placeholder")}
      options={[]}
    />
  );
};

const MULTILINE_TEXTFIELD_PROPS = {
  multiline: true,
  rows: 4,
  InputProps: { inputComponent: "textarea" as any },
  inputProps: { style: { resize: "vertical" as const } },
};

const DOUBLE_BRACE_RE = /^\{\{\s?([\s\S]*?)\s?\}\}$/;
const isWrappedInDoubleBraces = (value: unknown): boolean =>
  typeof value === "string" && DOUBLE_BRACE_RE.test(value);
const stripDoubleBraces = (value: string): string => {
  const match = value.match(DOUBLE_BRACE_RE);
  return match ? match[1] : value;
};
const wrapInDoubleBraces = (inner: string): string => `{{ ${inner} }}`;

const DoubleBraceFieldInput: FC<{
  value: any;
  placeholder?: string;
  useMultiline?: boolean;
  expressionLabel: string;
  onChange: (storedValue: string) => void;
}> = ({ value, placeholder, useMultiline, expressionLabel, onChange }) => {
  const wrapped = isWrappedInDoubleBraces(value);
  const inner = wrapped
    ? stripDoubleBraces(value as string)
    : typeof value === "string"
    ? value
    : value == null
    ? ""
    : String(value);

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        alignItems: "flex-start",
        flexDirection: "column",
      }}
    >
      <TextField
        type="text"
        fullWidth
        placeholder={placeholder}
        value={inner}
        onChange={(e) =>
          onChange(
            wrapped ? wrapInDoubleBraces(e.target.value) : e.target.value,
          )
        }
        {...(useMultiline ? MULTILINE_TEXTFIELD_PROPS : {})}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={wrapped}
            onChange={(e) =>
              onChange(e.target.checked ? wrapInDoubleBraces(inner) : inner)
            }
          />
        }
        label={expressionLabel}
      />
    </Box>
  );
};

const GenericJsonBuilder: FC<GenericJsonBuilderProps> = ({
  initialData,
  fieldOptions,
  enableSaveButton = false,
  enableDragDrop = false,
  label,
  onChange,
  renderAs,
  isArrayOfObjects = false,
  emptyArrayOfObjectsValue = "emptyArray",
  required = false,
  entryLayout = "vertical",
  ...props
}) => {
  const { t } = useTranslation();
  label = label || t("json_builder");

  const toTextFieldValue = (v: any): string | number => {
    if (typeof v === "string" || typeof v === "number") return v;
    if (typeof v === "boolean") return String(v);
    if (v === null || typeof v === "undefined") return "";
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  };

  const processedFieldOptions: FieldOption[] = useMemo(() => {
    const processFieldOption = (
      opt: FieldOption | [string, any],
    ): FieldOption => {
      const fieldOpt = Array.isArray(opt)
        ? {
            key: opt[0],
            ...opt[1],
            key_alias: opt[1].key_alias,
            operators: opt[1].operator || null,
            type: opt[1].type || "string",
            predefined_values: opt[1].values || null,
            predefined_values_with_aliases:
              opt[1].predefined_values_with_aliases,
            allow_custom_input:
              opt[1].allow_custom_input !== undefined
                ? opt[1].allow_custom_input
                : true,
            use_multiline: opt[1].use_multiline ?? true,
            fields: opt[1].fields,
            renderKeyAs: opt[1].renderKeyAs || "default",
            allow_negative_numbers: opt[1].allow_negative_numbers ?? false,
            use_editor: opt[1].use_editor ?? false,
            is_not_operator: opt[1].is_not_operator ?? false,
          }
        : {
            ...opt,
            use_multiline: opt.use_multiline ?? true,
            allow_negative_numbers: opt.allow_negative_numbers ?? false,
          };

      return fieldOpt;
    };

    return Array.isArray(fieldOptions)
      ? fieldOptions.map(processFieldOption)
      : Object.entries(fieldOptions).map(processFieldOption);
  }, [fieldOptions]);

  const createEntryFromObjectField = (key: string, value: any): JsonEntry => {
    const wildcardOpt = Array.isArray(fieldOptions)
      ? fieldOptions.find((f) => f.key === "*")
      : undefined;
    const fieldOpt = Array.isArray(fieldOptions)
      ? fieldOptions.find((f) => f.key === key) || wildcardOpt
      : { key, type: "string" };
    let operator: string | null = null;
    let realValue = value;
    let nestedEntries: JsonEntry[] | undefined = undefined;
    let useCustom = false;
    let valueType: JsonEntry["valueType"] = undefined;

    if (value && typeof value === "object" && !Array.isArray(value)) {
      const keys = Object.keys(value);
      if (keys.length === 1 && !fieldOpt?.is_not_operator) {
        operator = keys[0];
        realValue = value[operator];

        if (typeof realValue === "boolean") {
          valueType = "bool";
        } else if (
          typeof realValue === "number" ||
          (typeof realValue === "string" && !isNaN(Number(realValue)))
        ) {
          valueType = "number";
          realValue =
            typeof realValue === "string" ? Number(realValue) : realValue;
        } else if (Array.isArray(realValue)) {
          const allNumbers =
            realValue.length > 0 &&
            realValue.every(
              (item) =>
                typeof item === "number" ||
                (typeof item === "string" && !isNaN(Number(item))),
            );
          valueType = allNumbers ? "array_number" : "array_string";
          if (allNumbers) {
            realValue = realValue.map((item) =>
              typeof item === "string" ? Number(item) : item,
            );
          }
        } else {
          valueType = "string";
        }
        if (
          operator &&
          fieldOpt?.operatorsExpectingStringValuesOnly?.includes(operator) &&
          valueType !== "string"
        ) {
          valueType = "string";
          realValue =
            typeof realValue === "string" ? realValue : String(realValue);
        }
      }
    } else {
      if (typeof realValue === "boolean") {
        valueType = "bool";
      } else if (
        typeof realValue === "number" ||
        (typeof realValue === "string" && !isNaN(Number(realValue)))
      ) {
        valueType = "number";
        realValue =
          typeof realValue === "string" ? Number(realValue) : realValue;
      } else if (Array.isArray(realValue)) {
        const allNumbers =
          realValue.length > 0 &&
          realValue.every(
            (item) =>
              typeof item === "number" ||
              (typeof item === "string" && !isNaN(Number(item))),
          );
        valueType = allNumbers ? "array_number" : "array_string";
        if (allNumbers) {
          realValue = realValue.map((item) =>
            typeof item === "string" ? Number(item) : item,
          );
        }
      } else {
        valueType = "string";
      }
    }

    const objectFieldsForOperator =
      operator && fieldOpt?.operatorsExpectingObjectValues?.[operator];
    if (objectFieldsForOperator) {
      if (
        realValue &&
        typeof realValue === "object" &&
        !Array.isArray(realValue)
      ) {
        nestedEntries = objectFieldsForOperator.map((subField: any) => ({
          fieldKey: subField.key,
          value: (realValue as any)[subField.key] ?? "",
          operator: null,
        }));
      } else {
        nestedEntries = objectFieldsForOperator.map((subField: any) => ({
          fieldKey: subField.key,
          value: "",
          operator: null,
        }));
      }
    }

    if (
      fieldOpt?.type === "object" &&
      fieldOpt.fields &&
      typeof realValue === "object"
    ) {
      if (fieldOpt?.is_not_operator) {
        nestedEntries = Object.entries(realValue).map(
          ([nestedKey, nestedValue]) => ({
            fieldKey: nestedKey,
            value: nestedValue,
            operator: null,
          }),
        );
      } else {
        nestedEntries = fieldOpt.fields.map((subField: any) => ({
          fieldKey: subField.key,
          value: realValue[subField.key] ?? "",
          operator: null,
        }));
      }
    }

    if (fieldOpt?.predefined_values_with_aliases) {
      if (
        !fieldOpt.predefined_values_with_aliases.some(
          (item: { key: any }) => item.key === realValue,
        )
      ) {
        useCustom = true;
      }
    } else if (fieldOpt?.predefined_values) {
      if (!fieldOpt.predefined_values.includes(realValue)) {
        useCustom = true;
      }
    }

    if (
      fieldOpt?.type === "array" &&
      fieldOpt.fields &&
      Array.isArray(realValue)
    ) {
      const newArray = realValue.map((obj: any) => {
        const newObj = { ...obj };
        fieldOpt.fields.forEach((subField: any) => {
          if (subField.predefined_values_with_aliases) {
            newObj[`__useCustom_${subField.key}`] =
              !subField.predefined_values_with_aliases.some(
                (item: { key: any }) => item.key === newObj[subField.key],
              );
          } else if (subField.predefined_values) {
            newObj[`__useCustom_${subField.key}`] =
              !subField.predefined_values.includes(newObj[subField.key]);
          }
        });
        return newObj;
      });
      realValue = newArray;
    }

    if (
      fieldOpt?.renderValueInputAsSearchableDropdown &&
      typeof realValue === "string"
    ) {
      realValue = { value: realValue, label: realValue };
    }

    return {
      fieldKey: key,
      operator,
      value: realValue,
      nestedEntries,
      useCustom,
      customKey: fieldOpt && fieldOpt.renderKeyAs === "textfield",
      valueType,
    } as JsonEntry;
  };

  const normalizeInitialDataToEntries = (data: any): JsonEntry[] => {
    if (!data) return [];

    if (isArrayOfObjects && Array.isArray(data)) {
      return data.flatMap((item) => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
          return [];
        }

        return Object.entries(item).map(([key, value]) =>
          createEntryFromObjectField(key, value),
        );
      });
    }

    if (Array.isArray(data)) return data;
    if (typeof data === "object") {
      return Object.entries(data).map(([key, value]) =>
        createEntryFromObjectField(key, value),
      );
    }

    return [];
  };

  const initialDataProcessed = useRef(false);

  useEffect(() => {
    if (!initialDataProcessed.current) {
      setJsonEntries(normalizeInitialDataToEntries(initialData));
      initialDataProcessed.current = true;
    }
  }, [initialData, fieldOptions]);

  const [jsonEntries, setJsonEntries] = useState<JsonEntry[]>(() => {
    return normalizeInitialDataToEntries(initialData);
  });

  useEffect(() => {
    if (onChange) {
      const transformedJson = transformEntriesToJson();
      onChange(transformedJson);
    }
  }, [jsonEntries, onChange]);

  const transformEntriesToJson = () => {
    const wildcardOpt = processedFieldOptions.find((f) => f.key === "*");
    const json = jsonEntries.reduce((acc, entry) => {
      let finalValue = entry.value;
      const fieldOpt =
        processedFieldOptions.find((f) => f.key === entry.fieldKey) ||
        wildcardOpt;
      if (fieldOpt) {
        if (fieldOpt.renderValueInputAsSearchableDropdown) {
          finalValue =
            entry.value &&
            typeof entry.value === "object" &&
            "value" in entry.value
              ? entry.value.value
              : entry.value;
        }
        if (fieldOpt.type === "array") {
          if (fieldOpt.fields && Array.isArray(finalValue)) {
            finalValue = finalValue.map((obj) => {
              const cleaned = { ...obj };
              Object.keys(cleaned).forEach((key) => {
                if (key.startsWith("__useCustom_")) {
                  delete cleaned[key];
                }
              });
              return cleaned;
            });
          } else if (typeof finalValue === "string") {
            finalValue = finalValue
              .split(",")
              .map((item) => item.trim())
              .filter((item) => item !== "");
          }
        }
        const objectFieldsForOperator = entry.operator
          ? fieldOpt?.operatorsExpectingObjectValues?.[entry.operator]
          : undefined;

        if (
          (fieldOpt.type === "object" || objectFieldsForOperator) &&
          entry.nestedEntries
        ) {
          const effectiveObjectFields =
            objectFieldsForOperator || fieldOpt.fields;

          const visibleNestedEntries = entry.nestedEntries.filter(
            (nestedEntry) => {
              const nestedFieldOpt = effectiveObjectFields?.find(
                (f) => f.key === nestedEntry.fieldKey,
              );
              if (!nestedFieldOpt?.dependencies) return true;

              return !nestedFieldOpt.dependencies.some((dependency) => {
                const dependentField = jsonEntries.find(
                  (e) => e.fieldKey === dependency.dependsOn,
                );
                return (
                  dependentField && dependency.condition(dependentField.value)
                );
              });
            },
          );

          const allEmpty = visibleNestedEntries.every(
            (subEntry) => subEntry.value === "",
          );
          if (allEmpty) {
            finalValue = {};
          } else {
            finalValue = visibleNestedEntries.reduce((obj, subEntry) => {
              obj[subEntry.fieldKey] = subEntry.value;
              return obj;
            }, {} as any);
          }
        }
      }
      if (
        entry.operator &&
        fieldOpt?.operatorsExpectingStringValuesOnly?.includes(entry.operator)
      ) {
        finalValue =
          typeof finalValue === "string"
            ? finalValue
            : String(finalValue ?? "");
      }
      const operator = entry.operator;
      const fixedKey =
        typeof operator === "string" && fieldOpt?.fixedKeyOperatorGroups
          ? fieldOpt.fixedKeyOperatorGroups.find((g) =>
              g.operators.includes(operator),
            )?.fixedKey
          : undefined;
      const outputKey = fixedKey ?? entry.fieldKey;
      if (isArrayOfObjects && outputKey.trim() === "") {
        return acc;
      }
      const outputValue = entry.operator
        ? { [entry.operator]: finalValue }
        : finalValue;
      if (isArrayOfObjects) {
        acc.push({ [outputKey]: outputValue });
        return acc;
      }

      acc[outputKey] = outputValue;
      return acc;
    }, (isArrayOfObjects ? [] : {}) as any);

    if (isArrayOfObjects && json.length === 0) {
      return emptyArrayOfObjectsValue === "singleEmptyObject" ? [{}] : [];
    }

    return json;
  };

  const addNewEntry = () => {
    setJsonEntries([
      ...jsonEntries,
      { fieldKey: "", operator: null, value: "" },
    ]);
  };

  const handleFieldDependencies = (entry: JsonEntry) => {
    const fieldOpt = processedFieldOptions.find(
      (f) => f.key === entry.fieldKey,
    );
    if (!fieldOpt?.dependencies) return;

    const newEntries = [...jsonEntries];

    fieldOpt.dependencies.forEach((dependency) => {
      const dependentField = newEntries.find(
        (e) => e.fieldKey === dependency.dependsOn,
      );
      if (!dependentField) return;

      if (dependency.condition(dependentField.value)) {
        switch (dependency.action) {
          case "reset":
            entry.value = "";
            break;
          case "set":
            if (dependency.value !== undefined) {
              entry.value = dependency.value;
            }
            break;
          case "hide":
            entry.hidden = true;
            break;
        }
      } else {
        entry.hidden = false;
      }
    });

    setJsonEntries(newEntries);
  };

  const updateEntry = (index: number, updatedFields: Partial<JsonEntry>) => {
    const newEntries = [...jsonEntries];
    newEntries[index] = { ...newEntries[index], ...updatedFields };
    setJsonEntries(newEntries);

    handleFieldDependencies(newEntries[index]);
  };

  const deleteEntry = (index: number) => {
    const newEntries = jsonEntries.filter((_, i) => i !== index);
    setJsonEntries(newEntries);
  };

  const renderValueInput = (
    entry: JsonEntry,
    index: number,
    fieldOpt: FieldOption,
  ) => {
    if (fieldOpt.renderValueInputAsSearchableDropdown) {
      return (
        <SearchableDropdownValueInput
          entry={entry}
          index={index}
          fieldOpt={fieldOpt}
          updateEntry={updateEntry}
        />
      );
    }

    if (fieldOpt.predefined_values || fieldOpt.predefined_values_with_aliases) {
      if (fieldOpt.type === "array") {
        return (
          <FormControl fullWidth>
            <Select
              multiple
              value={Array.isArray(entry.value) ? entry.value : []}
              onChange={(e) =>
                updateEntry(index, { value: e.target.value as string[] })
              }
              input={<OutlinedInput label={t("select_multiple")} />}
              renderValue={(selected) => (selected as string[]).join(", ")}
            >
              {(fieldOpt.predefined_values as any[]).map((val, idx) => (
                <MenuItem key={idx} value={val}>
                  {String(val)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      } else {
        return (
          <FormControl fullWidth>
            <Select
              value={entry.useCustom ? "custom" : entry.value || ""}
              onChange={(e) => {
                const selected = e.target.value;
                if (selected === "custom") {
                  updateEntry(index, { useCustom: true, value: "" });
                } else {
                  updateEntry(index, { useCustom: false, value: selected });
                }
              }}
            >
              {(
                fieldOpt.predefined_values_with_aliases ||
                (fieldOpt.predefined_values
                  ? fieldOpt.predefined_values.map((val) => ({
                      key: val,
                      alias: String(val),
                    }))
                  : [])
              ).map((item, idx) => (
                <MenuItem key={idx} value={item.key}>
                  {item.alias}
                </MenuItem>
              ))}
              {fieldOpt.allow_custom_input && (
                <MenuItem value="custom">{t("custom")}</MenuItem>
              )}
            </Select>
            {entry.useCustom && (
              <TextField
                label={t("custom")}
                type="text"
                fullWidth
                value={toTextFieldValue(entry.value)}
                onChange={(e) => updateEntry(index, { value: e.target.value })}
              />
            )}
          </FormControl>
        );
      }
    }

    if (fieldOpt.useJsonEditor) {
      return (
        <JsonEditor
          label={fieldOpt.jsonEditorParameters?.label}
          noLabel={fieldOpt.jsonEditorParameters?.noLabel}
          noTopBar={fieldOpt.jsonEditorParameters?.noTopBar}
          noBottomBar={fieldOpt.jsonEditorParameters?.noBottomBar}
          value={entry.value}
          onSave={(updatedJson) => updateEntry(index, { value: updatedJson })}
        />
      );
    }

    if (
      fieldOpt.valueTypesForInputToggle?.includes("bool") &&
      entry.valueType === "bool"
    ) {
      return (
        <FormControl component="fieldset">
          <RadioGroup
            row
            value={String(entry.value)}
            onChange={(e) => {
              updateEntry(index, { value: e.target.value === "true" });
            }}
          >
            <FormControlLabel value="true" control={<Radio />} label="True" />
            <FormControlLabel value="false" control={<Radio />} label="False" />
          </RadioGroup>
        </FormControl>
      );
    }

    switch (fieldOpt.type) {
      case "boolean":
        return (
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={entry.value === "" ? "" : String(entry.value)}
              onChange={(e) => {
                updateEntry(index, { value: e.target.value === "true" });
              }}
            >
              <FormControlLabel value="true" control={<Radio />} label="True" />
              <FormControlLabel
                value="false"
                control={<Radio />}
                label="False"
              />
            </RadioGroup>
          </FormControl>
        );
      case "number":
        return (
          <TextField
            type="text"
            fullWidth
            value={entry.value === 0 ? "0" : entry.value || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value;
              if (val === "" || /^-?\d*$/.test(val)) {
                updateEntry(index, {
                  value: val === "" ? 0 : Number(val),
                });
              }
            }}
            inputProps={{
              min: fieldOpt.allow_negative_numbers ? undefined : 0,
              inputMode: "numeric",
              pattern: "-?[0-9]*",
            }}
          />
        );
      case "string":
        return (
          <TextField
            type="text"
            fullWidth
            value={toTextFieldValue(entry.value)}
            onChange={(e) => updateEntry(index, { value: e.target.value })}
            placeholder={fieldOpt.placeholder}
            {...(fieldOpt.use_multiline ? MULTILINE_TEXTFIELD_PROPS : {})}
          />
        );
      case "array":
        if (fieldOpt.fields) {
          return (
            <Box sx={{ border: "1px solid #ccc", padding: 1, marginTop: 1 }}>
              {(Array.isArray(entry.value) ? entry.value : []).map(
                (obj, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      mb: 1,
                      borderBottom: "1px solid #ccc",
                      paddingBottom: 1,
                    }}
                  >
                    {fieldOpt.fields?.map((subField, subIndex) => (
                      <Box
                        key={subIndex}
                        sx={{
                          display: "block",
                          gap: 1,
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Typography variant="caption">
                          {subField.key_alias || subField.key}
                        </Typography>
                        {subField.predefined_values ? (
                          <FormControl fullWidth>
                            <Select
                              value={
                                obj[`__useCustom_${subField.key}`]
                                  ? "custom"
                                  : obj[subField.key] || ""
                              }
                              onChange={(e) => {
                                const selected = e.target.value;
                                const newArray = Array.isArray(entry.value)
                                  ? [...entry.value]
                                  : [];
                                if (selected === "custom") {
                                  newArray[idx] = {
                                    ...newArray[idx],
                                    [subField.key]: "",
                                    [`__useCustom_${subField.key}`]: true,
                                  };
                                } else {
                                  newArray[idx] = {
                                    ...newArray[idx],
                                    [subField.key]: selected,
                                    [`__useCustom_${subField.key}`]: false,
                                  };
                                }
                                updateEntry(index, { value: newArray });
                              }}
                            >
                              {subField.predefined_values.map((val, pIdx) => (
                                <MenuItem key={pIdx} value={val}>
                                  {String(val)}
                                </MenuItem>
                              ))}
                              {subField.allow_custom_input && (
                                <MenuItem value="custom">Custom</MenuItem>
                              )}
                            </Select>
                            {(obj[`__useCustom_${subField.key}`] === true ||
                              (obj[subField.key] === "" &&
                                obj[`__useCustom_${subField.key}`] !==
                                  false)) && (
                              <TextField
                                type="text"
                                fullWidth
                                value={toTextFieldValue(obj[subField.key])}
                                onChange={(e) => {
                                  const newArray = Array.isArray(entry.value)
                                    ? [...entry.value]
                                    : [];
                                  newArray[idx] = {
                                    ...newArray[idx],
                                    [subField.key]: e.target.value,
                                    [`__useCustom_${subField.key}`]: true,
                                  };
                                  updateEntry(index, { value: newArray });
                                }}
                              />
                            )}
                          </FormControl>
                        ) : subField.wrappableInDoubleBraces ? (
                          <DoubleBraceFieldInput
                            value={obj[subField.key]}
                            placeholder={subField.placeholder}
                            useMultiline={subField.use_multiline}
                            expressionLabel={t("template_variable")}
                            onChange={(storedValue) => {
                              const newArray = Array.isArray(entry.value)
                                ? [...entry.value]
                                : [];
                              newArray[idx] = {
                                ...newArray[idx],
                                [subField.key]: storedValue,
                              };
                              updateEntry(index, { value: newArray });
                            }}
                          />
                        ) : (
                          <TextField
                            type="text"
                            fullWidth
                            placeholder={subField.placeholder}
                            value={toTextFieldValue(obj[subField.key])}
                            onChange={(e) => {
                              const newArray = Array.isArray(entry.value)
                                ? [...entry.value]
                                : [];
                              newArray[idx] = {
                                ...newArray[idx],
                                [subField.key]: e.target.value,
                              };
                              updateEntry(index, { value: newArray });
                            }}
                            {...(subField.use_multiline
                              ? MULTILINE_TEXTFIELD_PROPS
                              : {})}
                          />
                        )}
                      </Box>
                    ))}
                    <Button
                      onClick={() => {
                        const newArray = Array.isArray(entry.value)
                          ? [...entry.value]
                          : [];
                        newArray.splice(idx, 1);
                        updateEntry(index, { value: newArray });
                      }}
                    >
                      {t("remove")}
                    </Button>
                  </Box>
                ),
              )}
              <Button
                onClick={() => {
                  const newObject: any = {};
                  fieldOpt.fields?.forEach((sf) => {
                    newObject[sf.key] = sf.wrappableInDoubleBraces
                      ? wrapInDoubleBraces("")
                      : "";
                    newObject[`__useCustom_${sf.key}`] = false;
                  });
                  const newArray = Array.isArray(entry.value)
                    ? [...entry.value, newObject]
                    : [newObject];
                  updateEntry(index, { value: newArray });
                }}
              >
                {t("add_object")}
              </Button>
            </Box>
          );
        } else {
          return (
            <TextField
              type="text"
              fullWidth
              placeholder="Comma separated values"
              value={toTextFieldValue(entry.value)}
              onChange={(e) => updateEntry(index, { value: e.target.value })}
              {...(fieldOpt.use_multiline ? MULTILINE_TEXTFIELD_PROPS : {})}
            />
          );
        }
      case "object":
        if (fieldOpt.fields) {
          return (
            <Box sx={{ border: "1px solid #ccc", padding: 1, marginTop: 1 }}>
              {fieldOpt.fields.map((subField, subIndex) => {
                if (shouldHideNestedField(subField)) {
                  return null;
                }

                const nestedEntry = entry.nestedEntries?.find(
                  (ne) => ne.fieldKey === subField.key,
                ) || {
                  fieldKey: subField.key,
                  value: "",
                  operator: null,
                };
                return (
                  <Box key={subIndex} sx={{ marginBottom: 1 }}>
                    <Typography variant="caption">
                      {subField.key_alias || subField.key}
                    </Typography>
                    {renderNestedInput(
                      nestedEntry,
                      subField,
                      (updatedNested) => {
                        const newNested = entry.nestedEntries
                          ? [...entry.nestedEntries]
                          : [];
                        const existingIndex = newNested.findIndex(
                          (ne) => ne.fieldKey === subField.key,
                        );
                        if (existingIndex >= 0) {
                          newNested[existingIndex] = {
                            ...newNested[existingIndex],
                            ...updatedNested,
                          };
                        } else {
                          newNested.push({
                            fieldKey: subField.key,
                            value: "",
                            ...updatedNested,
                          });
                        }
                        updateEntry(index, { nestedEntries: newNested });
                      },
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        }
        return <Box>{t("no_nested_fields_defined")}</Box>;
      default:
        return null;
    }
  };

  const shouldHideNestedField = (subField: FieldOption) => {
    if (!subField.dependencies) return false;

    return subField.dependencies.some((dependency) => {
      const dependentField = jsonEntries.find(
        (e) => e.fieldKey === dependency.dependsOn,
      );
      return dependentField && dependency.condition(dependentField.value);
    });
  };

  const renderNestedInput = (
    nestedEntry: JsonEntry,
    subField: FieldOption,
    onUpdate: (updated: Partial<JsonEntry>) => void,
  ) => {
    if (shouldHideNestedField(subField)) {
      return null;
    }

    if (subField.predefined_values || subField.predefined_values_with_aliases) {
      if (subField.type === "array") {
        return (
          <FormControl fullWidth>
            <Select
              multiple
              value={Array.isArray(nestedEntry.value) ? nestedEntry.value : []}
              onChange={(e) => onUpdate({ value: e.target.value as string[] })}
              input={<OutlinedInput label={t("select_multiple")} />}
              renderValue={(selected) => (selected as string[]).join(", ")}
            >
              {(subField.predefined_values as any[]).map((val, idx) => (
                <MenuItem key={idx} value={val}>
                  {String(val)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      } else {
        return (
          <FormControl fullWidth>
            <Select
              value={nestedEntry.useCustom ? "custom" : nestedEntry.value || ""}
              onChange={(e) => {
                const selected = e.target.value;
                if (selected === "custom") {
                  onUpdate({ useCustom: true, value: "" });
                } else {
                  onUpdate({ useCustom: false, value: selected });
                }
              }}
            >
              {(
                subField.predefined_values_with_aliases ||
                (subField.predefined_values
                  ? subField.predefined_values.map((val) => ({
                      key: val,
                      alias: String(val),
                    }))
                  : [])
              ).map((item, idx) => (
                <MenuItem key={idx} value={item.key}>
                  {item.alias}
                </MenuItem>
              ))}
              {subField.allow_custom_input && (
                <MenuItem value="custom">{t("custom")}</MenuItem>
              )}
            </Select>
            {nestedEntry.useCustom && (
              <TextField
                label={t("custom")}
                type="text"
                fullWidth
                value={toTextFieldValue(nestedEntry.value)}
                onChange={(e) => onUpdate({ value: e.target.value })}
              />
            )}
          </FormControl>
        );
      }
    }

    switch (subField.type) {
      case "boolean":
        return (
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={nestedEntry.value === "" ? "" : String(nestedEntry.value)}
              onChange={(e) => onUpdate({ value: e.target.value === "true" })}
            >
              <FormControlLabel value="true" control={<Radio />} label="True" />
              <FormControlLabel
                value="false"
                control={<Radio />}
                label="False"
              />
            </RadioGroup>
          </FormControl>
        );
      case "number":
        return (
          <TextField
            type="text"
            fullWidth
            value={nestedEntry.value === 0 ? "0" : nestedEntry.value || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value;
              if (val === "" || /^-?\d*$/.test(val)) {
                onUpdate({
                  value: val === "" ? 0 : Number(val),
                });
              }
            }}
            inputProps={{
              min: subField.allow_negative_numbers ? undefined : 0,
              inputMode: "numeric",
              pattern: "-?[0-9]*",
            }}
          />
        );
      case "string":
        return (
          <TextField
            type="text"
            fullWidth
            value={toTextFieldValue(nestedEntry.value)}
            onChange={(e) => onUpdate({ value: e.target.value })}
            {...(subField.use_multiline ? MULTILINE_TEXTFIELD_PROPS : {})}
          />
        );
      case "array":
        return (
          <TextField
            type="text"
            fullWidth
            placeholder="Comma separated values"
            value={
              Array.isArray(nestedEntry.value)
                ? nestedEntry.value.join(", ")
                : toTextFieldValue(nestedEntry.value)
            }
            onChange={(e) =>
              onUpdate({
                value: e.target.value.split(",").map((item) => item.trim()),
              })
            }
            {...(subField.use_multiline ? MULTILINE_TEXTFIELD_PROPS : {})}
          />
        );
      case "object":
        return <Box>{t("no_nested_objects_allowed")}</Box>;
      default:
        return null;
    }
  };

  const isEmptyValue = (val: any): boolean => {
    if (val === null) return false;
    if (typeof val === "string") return val === "";
    if (Array.isArray(val)) return val.length === 0;
    if (typeof val === "object") return Object.keys(val).length === 0;
    return false;
  };

  const [forceDefined, setForceDefined] = useState(false);

  const renderValueInputToggle = (
    entry: JsonEntry,
    index: number,
    recognizedField: FieldOption,
  ) => {
    const currentValue = forceDefined
      ? "default"
      : entry.value === null
      ? "null"
      : isEmptyValue(entry.value)
      ? "empty"
      : "default";

    return (
      <ToggleButtonGroup
        value={currentValue}
        exclusive
        onChange={(e, newVal) => {
          if (!newVal) return;

          if (newVal === "null") {
            setForceDefined(false);
            updateEntry(index, {
              value: null,
              operator: null,
              nestedEntries: undefined,
            });
          } else if (newVal === "empty") {
            setForceDefined(false);
            let emptyVal: any;
            const fieldType = recognizedField?.type || "string";
            switch (fieldType) {
              case "object":
                emptyVal = {};
                updateEntry(index, {
                  value: emptyVal,
                  nestedEntries: undefined,
                });
                break;
              case "array":
                emptyVal = [];
                updateEntry(index, { value: emptyVal });
                break;
              default:
                emptyVal = "";
                updateEntry(index, { value: emptyVal });
            }
          } else if (newVal === "default") {
            setForceDefined(true);
            let defaultVal: any;
            const fieldType = recognizedField?.type || "string";
            switch (fieldType) {
              case "object":
                defaultVal = {};
                updateEntry(index, {
                  value: defaultVal,
                  nestedEntries: undefined,
                });
                break;
              case "array":
                defaultVal = [];
                updateEntry(index, { value: defaultVal });
                break;
              case "boolean":
                defaultVal = false;
                updateEntry(index, { value: defaultVal });
                break;
              case "number":
                defaultVal = 0;
                updateEntry(index, { value: defaultVal });
                break;
              default:
                defaultVal = "";
                updateEntry(index, { value: defaultVal });
            }
          }
        }}
        size="small"
        sx={{ mb: 1 }}
      >
        <ToggleButton value="default">{t("defined_value")}</ToggleButton>
        <ToggleButton value="null">{t("null")}</ToggleButton>
        <ToggleButton value="empty">{t("empty_value")}</ToggleButton>
      </ToggleButtonGroup>
    );
  };

  const hideEntry = (entry: JsonEntry) => {
    const fieldOpt = processedFieldOptions.find(
      (f) => f.key === entry.fieldKey,
    );
    if (!fieldOpt) return false;

    if (fieldOpt.dependencies) {
      return fieldOpt.dependencies.some((dependency) => {
        const dependentField = jsonEntries.find(
          (e) => e.fieldKey === dependency.dependsOn,
        );
        return dependentField && dependency.condition(dependentField.value);
      });
    }

    return fieldOpt.hide || false;
  };

  const renderEntry = (entry: JsonEntry, index: number) => {
    const recognizedField = processedFieldOptions.find(
      (f) => f.key === entry.fieldKey,
    );
    const wildcardField = processedFieldOptions.find((f) => f.key === "*");
    const shouldShowValueInput =
      entry.value !== null &&
      (wildcardField || forceDefined || !isEmptyValue(entry.value));

    if (!recognizedField && !wildcardField) {
      return (
        <Box
          key={index}
          sx={{
            alignItems: "center",
            display: renderAs === "form" ? "grid" : "flex",
            gridTemplateColumns:
              renderAs === "dropdown" ? "1fr 1fr 1fr" : undefined,
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="body1">{entry.fieldKey}</Typography>
          </Box>
          <TextField
            type="text"
            fullWidth
            {...MULTILINE_TEXTFIELD_PROPS}
            value={toTextFieldValue(entry.value)}
            onChange={(e) => updateEntry(index, { value: e.target.value })}
            placeholder={t("enter_value")}
          />
        </Box>
      );
    }

    const fieldToUse = recognizedField || wildcardField;
    if (!fieldToUse) return null;

    const getOperatorLabel = (op: string) =>
      fieldToUse.operator_aliases?.[op] || op;

    const isHorizontalEntry =
      renderAs === "form" && entryLayout === "horizontal";

    return (
      <Box
        key={index}
        sx={{
          alignItems: "center",
          display: isHorizontalEntry
            ? "grid"
            : renderAs === "form"
            ? "grid"
            : "flex",
          ...(isHorizontalEntry
            ? {
                gridTemplateColumns: "1fr 1fr 1fr",
              }
            : {
                gridTemplateColumns:
                  renderAs === "dropdown" ? "1fr 1fr 1fr" : undefined,
              }),
          gap: 1,
        }}
      >
        {fieldToUse.renderValueInputToggle && (
          <Box sx={isHorizontalEntry ? { gridColumn: "1 / -1" } : undefined}>
            {renderValueInputToggle(entry, index, fieldToUse)}
          </Box>
        )}
        {fieldToUse.renderValueInputTypeToggle && (
          <Box sx={isHorizontalEntry ? { gridColumn: "1 / -1" } : undefined}>
            {renderValueInputTypeToggle(entry, index, fieldToUse)}
          </Box>
        )}

        {/* Render key */}
        {fieldToUse === wildcardField ? (
          (() => {
            const operator = entry.operator;
            const fixedKey =
              typeof operator === "string" && fieldToUse.fixedKeyOperatorGroups
                ? fieldToUse.fixedKeyOperatorGroups.find((g) =>
                    g.operators.includes(operator),
                  )?.fixedKey
                : undefined;
            return fixedKey != null ? (
              <Box
                sx={{
                  cursor: "not-allowed",
                  width: "100%",
                  "& .MuiOutlinedInput-root, & .MuiOutlinedInput-input": {
                    cursor: "not-allowed",
                  },
                }}
              >
                <TextField
                  value={fixedKey}
                  disabled
                  fullWidth
                  {...MULTILINE_TEXTFIELD_PROPS}
                  {...(isHorizontalEntry ? { rows: 3 } : {})}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "grey.100",
                      color: "text.primary",
                    },
                    "& .MuiOutlinedInput-input": {
                      color: "text.primary",
                    },
                  }}
                  InputProps={{
                    ...MULTILINE_TEXTFIELD_PROPS.InputProps,
                    readOnly: true,
                  }}
                />
              </Box>
            ) : fieldToUse.key_suggestions &&
              fieldToUse.key_suggestions.length > 0 ? (
              <Autocomplete
                freeSolo
                fullWidth
                options={fieldToUse.key_suggestions}
                getOptionLabel={(option: any) =>
                  typeof option === "string" ? option : option.value
                }
                filterOptions={createFilterOptions({
                  stringify: (option: any) =>
                    `${option.value} ${option.label ?? ""}`,
                })}
                inputValue={entry.fieldKey || ""}
                onInputChange={(_event, newInputValue) => {
                  if (newInputValue === (entry.fieldKey || "")) {
                    return;
                  }
                  updateEntry(index, {
                    fieldKey: newInputValue,
                    customKey: true,
                    operator: fieldToUse.operators?.[0] || null,
                  });
                }}
                renderOption={(optionProps, option: any) => (
                  <li {...optionProps} key={option.value}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography variant="body2">{option.value}</Typography>
                      {option.label && (
                        <Typography variant="caption" color="text.secondary">
                          {option.label}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} placeholder={t("enter_key")} />
                )}
              />
            ) : (
              <TextField
                value={entry.fieldKey || ""}
                onChange={(e) =>
                  updateEntry(index, {
                    fieldKey: e.target.value,
                    customKey: true,
                    operator: fieldToUse.operators?.[0] || null,
                  })
                }
                placeholder={t("enter_key")}
                fullWidth
                {...MULTILINE_TEXTFIELD_PROPS}
                {...(isHorizontalEntry ? { rows: 3 } : {})}
                InputProps={
                  isHorizontalEntry
                    ? {
                        ...MULTILINE_TEXTFIELD_PROPS.InputProps,
                        sx: {
                          "& textarea": {
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          },
                        },
                      }
                    : MULTILINE_TEXTFIELD_PROPS.InputProps
                }
              />
            );
          })()
        ) : (
          <Box>
            <Typography variant="body1">
              {fieldToUse.key_alias || entry.fieldKey}
            </Typography>
          </Box>
        )}

        {/* Operator selection */}
        {fieldToUse.operators &&
          Array.isArray(fieldToUse.operators) &&
          fieldToUse.operators.length > 0 && (
            <FormControl sx={{ minWidth: 120 }}>
              <Select
                value={entry.operator || ""}
                displayEmpty
                onChange={(e) => {
                  const newOperator = e.target.value;
                  const fixedKey = fieldToUse.fixedKeyOperatorGroups?.find(
                    (g) => g.operators.includes(newOperator),
                  )?.fixedKey;
                  const objectFieldsForOperator =
                    fieldToUse.operatorsExpectingObjectValues?.[newOperator];

                  if (objectFieldsForOperator) {
                    updateEntry(index, {
                      operator: newOperator,
                      valueType: "string",
                      value: {},
                      nestedEntries: objectFieldsForOperator.map((sf) => ({
                        fieldKey: sf.key,
                        value: "",
                        operator: null,
                      })),
                      ...(fixedKey != null ? { fieldKey: fixedKey } : {}),
                    });
                    return;
                  }

                  let newValueType: JsonEntry["valueType"] = entry.valueType;
                  if (
                    fieldToUse.operatorsExpectingStringValuesOnly?.includes(
                      newOperator,
                    )
                  ) {
                    newValueType = "string";
                  } else {
                    switch (newOperator) {
                      case "is":
                      case "not":
                      case "contains":
                        newValueType = "string";
                        break;
                      case "gt":
                      case "lt":
                        newValueType = "number";
                        break;
                      case "in":
                      case "nin":
                        newValueType = entry.valueType?.startsWith("array_")
                          ? entry.valueType
                          : "array_string";
                        break;
                    }
                  }

                  const updates: Partial<JsonEntry> = {
                    operator: newOperator,
                    valueType: newValueType,
                    value: "",
                  };
                  if (fixedKey != null) {
                    updates.fieldKey = fixedKey;
                  }
                  updateEntry(index, updates);
                }}
              >
                <MenuItem value="" disabled>
                  {t("select_operator")}
                </MenuItem>
                {fieldToUse.operators.map((op, idx) => (
                  <MenuItem key={idx} value={op}>
                    {getOperatorLabel(op)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

        {/* Value input */}
        {shouldShowValueInput && (
          <Box sx={{ flex: 1 }}>
            {entry.valueType === "number" && !fieldToUse.predefined_values ? (
              <TextField
                type="text"
                fullWidth
                value={entry.value === 0 ? "0" : entry.value || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const val = e.target.value;
                  if (val === "" || /^-?\d*$/.test(val)) {
                    updateEntry(index, {
                      value: val === "" ? 0 : Number(val),
                    });
                  }
                }}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "-?[0-9]*",
                  min: fieldToUse.allow_negative_numbers ? undefined : 0,
                }}
              />
            ) : entry.valueType?.startsWith("array_") &&
              !fieldToUse.fields &&
              !fieldToUse.predefined_values ? (
              <TextField
                type="text"
                fullWidth
                multiline
                rows={6}
                value={
                  Array.isArray(entry.value)
                    ? entry.value.join("\n")
                    : entry.value || ""
                }
                onChange={(e) => {
                  const lines = e.target.value.split("\n");
                  const newValues =
                    entry.valueType === "array_number"
                      ? lines
                          .map((v) => {
                            const trimmed = v.trim();
                            return trimmed === ""
                              ? undefined
                              : isNaN(Number(trimmed))
                              ? undefined
                              : Number(trimmed);
                          })
                          .filter((v) => v !== undefined)
                      : lines;
                  updateEntry(index, { value: newValues });
                }}
                placeholder={
                  entry.valueType === "array_number"
                    ? t("enter_numbers_per_line")
                    : t("enter_values_per_line")
                }
                InputProps={{
                  inputComponent: "textarea" as any,
                  sx: {
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                  },
                }}
                inputProps={{ style: { resize: "vertical" } }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    const target = e.target as HTMLTextAreaElement;
                    const value = target.value;
                    const selectionStart = target.selectionStart;
                    const newValue =
                      value.slice(0, selectionStart) +
                      "\n" +
                      value.slice(target.selectionEnd);
                    const values = newValue.split("\n").map((v) => v.trim());
                    const newValues =
                      entry.valueType === "array_number"
                        ? values.map((v) => Number(v) || 0)
                        : values;
                    updateEntry(index, { value: newValues });
                    setTimeout(() => {
                      target.selectionStart = target.selectionEnd =
                        selectionStart + 1;
                    }, 0);
                  }
                }}
              />
            ) : (
              (() => {
                const operatorObjectFields = entry.operator
                  ? fieldToUse.operatorsExpectingObjectValues?.[entry.operator]
                  : undefined;
                return renderValueInput(
                  entry,
                  index,
                  operatorObjectFields
                    ? ({
                        ...fieldToUse,
                        type: "object",
                        fields: operatorObjectFields,
                      } as FieldOption)
                    : fieldToUse,
                );
              })()
            )}
          </Box>
        )}
      </Box>
    );
  };

  const resetEntry = (index: number) => {
    const newEntries = [...jsonEntries];
    newEntries[index] = { fieldKey: "", operator: null, value: "" };
    setJsonEntries(newEntries);
  };

  const handleReorder = (result: any) => {
    const { source, destination } = result;
    if (!destination) return;

    const reorderedEntries = Array.from(jsonEntries);
    const [removed] = reorderedEntries.splice(source.index, 1);
    reorderedEntries.splice(destination.index, 0, removed);

    setJsonEntries(reorderedEntries);
  };

  const formContext = useFormContext();
  useEffect(() => {
    if (formContext && props.name) {
      formContext.register(props.name);

      const newValue = transformEntriesToJson();
      formContext.setValue(props.name, newValue, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      formContext.trigger();
    }
  }, [formContext, props.name, jsonEntries]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderValueInputTypeToggle = (
    entry: JsonEntry,
    index: number,
    fieldOpt: FieldOption,
  ) => {
    const currentType = entry.valueType || "string";

    const getTypeOptionsForOperator = (operator: string | null | undefined) => {
      const defaultTypes = fieldOpt.valueTypesForInputToggle || [
        "string",
        "number",
        "array_string",
        "array_number",
      ];

      if (operator && fieldOpt.operatorsExpectingObjectValues?.[operator]) {
        return defaultTypes.filter((type) => type === "string");
      }
      if (
        operator &&
        fieldOpt.operatorsExpectingStringValuesOnly?.includes(operator)
      ) {
        return defaultTypes.filter((type) => type === "string");
      }
      switch (operator) {
        case "gt":
        case "lt":
          return defaultTypes.filter((type) => type === "number");
        case "in":
        case "nin":
          return defaultTypes.filter((type) => type.startsWith("array_"));
        default:
          return defaultTypes;
      }
    };

    const typeOptions = getTypeOptionsForOperator(entry.operator);

    const getTypeLabel = (type: string) => {
      switch (type) {
        case "string":
          return t("string");
        case "number":
          return t("number");
        case "array_string":
          return t("string_list");
        case "array_number":
          return t("number_list");
        case "bool":
          return t("boolean");
        default:
          return type;
      }
    };

    if (typeOptions.length <= 1) return null;

    return (
      <Box sx={{ mb: 1 }}>
        <ToggleButtonGroup
          value={currentType}
          exclusive
          onChange={(e, newType) => {
            if (!newType) return;

            let newValue: any = entry.value;

            if (typeof entry.value === "string") {
              switch (newType) {
                case "number":
                  newValue = !isNaN(Number(entry.value))
                    ? Number(entry.value)
                    : 0;
                  break;
                case "array_string":
                  newValue = entry.value
                    ? entry.value.split("\n").filter(Boolean)
                    : [];
                  break;
                case "array_number":
                  newValue = entry.value
                    ? entry.value
                        .split("\n")
                        .map((v) => {
                          const trimmed = v.trim();
                          return trimmed === "" ? null : Number(trimmed);
                        })
                        .filter((v) => v !== null && !isNaN(v))
                    : [];
                  break;
                case "bool":
                  newValue = entry.value.toLowerCase() === "true";
                  break;
              }
            } else if (typeof entry.value === "number") {
              switch (newType) {
                case "string":
                  newValue = String(entry.value);
                  break;
                case "array_string":
                  newValue = [String(entry.value)];
                  break;
                case "array_number":
                  newValue = [entry.value];
                  break;
                case "bool":
                  newValue = entry.value !== 0;
                  break;
              }
            } else if (Array.isArray(entry.value)) {
              switch (newType) {
                case "string":
                  newValue = entry.value.join("\n");
                  break;
                case "number":
                  newValue = Number(entry.value[0]) || 0;
                  break;
                case "array_string":
                  newValue = entry.value.map((v) => String(v));
                  break;
                case "array_number":
                  newValue = entry.value
                    .map((v) => {
                      const num = Number(v);
                      return isNaN(num) ? null : num;
                    })
                    .filter((v) => v !== null);
                  break;
                case "bool":
                  newValue = entry.value.length > 0;
                  break;
              }
            } else if (typeof entry.value === "boolean") {
              switch (newType) {
                case "string":
                  newValue = String(entry.value);
                  break;
                case "number":
                  newValue = entry.value ? 1 : 0;
                  break;
                case "array_string":
                  newValue = [String(entry.value)];
                  break;
                case "array_number":
                  newValue = [entry.value ? 1 : 0];
                  break;
              }
            }

            updateEntry(index, { valueType: newType, value: newValue });
          }}
          size="small"
        >
          {typeOptions.map((type) => (
            <ToggleButton key={type} value={type}>
              {getTypeLabel(type)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    );
  };

  return (
    <>
      <GenericJsonBuilderWrapper
        addNewEntry={addNewEntry}
        resetEntry={resetEntry}
        deleteEntry={deleteEntry}
        jsonEntries={jsonEntries}
        renderItems={renderEntry}
        enableDragDrop={enableDragDrop}
        onReorder={handleReorder}
        transformedJson={transformEntriesToJson()}
        enableSaveButton={enableSaveButton}
        onSave={props.onSave}
        label={label}
        required={required}
        entryLayout={entryLayout}
        hideEntries={hideEntry}
        {...props}
      />
    </>
  );
};

export default GenericJsonBuilder;
